import { isWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import { withErrorBoundary } from 'app/components/error-boundary';
import { images } from 'app/design/images';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { ProgressIndicator } from 'app/design/primitives/progress-indicator';
import { Text } from 'app/design/primitives/text';

import { useDeviceHeight } from 'app/hooks/use-device-size';
import { useOnboarding } from 'app/hooks/use-onboarding';
import { ContactInformationSteps } from 'app/screens/onboarding/contact-information-steps';
import { useOnboardingNavigation } from 'app/screens/onboarding/credentials';
import { useContactInformationHeaderOptions } from 'app/screens/onboarding/hooks/use-contact-information-header-options';
import { useContactInformationScreen } from 'app/screens/onboarding/hooks/use-contact-information-screen';
import {
  ONBOARDING_LARGE_OVERLAY_HEADER_HEIGHT,
  onboardingLargeModalHeight,
} from 'app/screens/onboarding/onboarding-large-modal-layout';
import { SignUpSheet, SignUpSource } from 'app/screens/onboarding/signup';
import type { NavigationScreen } from 'app/types/navigation';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, useWindowDimensions, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();
const CONTENT_CONTAINER_STYLE = { paddingBottom: theme.spacing['spacing.general.x2'] };

const ContactInformationScreenComponent: NavigationScreen<'contact-information'> = ({ route, navigation }) => {
  const s = useContactInformationScreen({ navigation, route });
  const { current, max } = useOnboarding();
  const { t: tOnboarding } = useTranslation('onboarding');

  useOnboardingNavigation(s.route.params?.userType, { headerPresentation: 'overlay' });
  useContactInformationHeaderOptions(navigation, {
    headerVariant: 'overlay',
    hideCross: route.params?.hideCross,
    onBackPress: s.onBackPress,
    step: s.step,
  });

  const deviceHeight = useDeviceHeight();
  const { height: windowHeight } = useWindowDimensions();
  const modalHeight = onboardingLargeModalHeight(windowHeight);
  const insets = useSafeAreaInsets();

  return (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
      <Column backgroundColor="text.primary" flex={1}>
        <Column bottom={0} left={0} position="absolute" right={0} top={0}>
          <PCImage cachePolicy="memory-disk" contentFit="cover" flex={1} source={images.web.contact_information} />
        </Column>
        <Column
          backgroundColor="text.primary"
          bottom={0}
          left={0}
          opacity={0.42}
          position="absolute"
          right={0}
          top={0}
        />

        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            paddingHorizontal: theme.spacing['spacing.general.x4'],
            paddingTop: ONBOARDING_LARGE_OVERLAY_HEADER_HEIGHT,
          }}
        >
          <Column
            alignSelf="center"
            backgroundColor="surface.default"
            borderRadius="radius.xl"
            elevation={6}
            maxWidth={480}
            paddingHorizontal="spacing.general.x8"
            paddingTop="spacing.general.x8"
            gap="spacing.general.x4"
            shadowColor="black"
            shadowOffset={{ height: 4, width: 0 }}
            shadowOpacity={0.12}
            shadowRadius={16}
            style={{ height: modalHeight }}
            width="100%"
          >
            <ScrollView
              contentContainerStyle={CONTENT_CONTAINER_STYLE}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
              style={{ flex: 1 }}
            >
              <ProgressIndicator activeColor={s.progressActiveColor} index={current} length={max} />

              <Text
                fontWeight="600"
                marginBottom="spacing.general.x2"
                marginTop="spacing.general.x4"
                textAlign="center"
                variant="headerXSmall"
              >
                {isWhiteLabel(WhiteLabel.Refrain) ? s.t('signupTitle', { ns: 'festival' }) : s.t(`title${s.tSuffix}`)}
              </Text>

              <Column
                backgroundColor={isWhiteLabel(WhiteLabel.Refrain) ? undefined : 'surface.primary'}
                borderRadius="radius.lg"
                padding={isWhiteLabel(WhiteLabel.Refrain) ? 'spacing.general.0' : 'spacing.general.x4'}
              >
                <ContactInformationSteps
                  control={s.control}
                  deviceHeight={deviceHeight}
                  flow={s.flowRest}
                  t={s.t}
                  tAPI={s.tAPI}
                  tSuffix={s.tSuffix}
                  variant="highlight"
                />
              </Column>
            </ScrollView>

            <Column
              gap="spacing.general.x2"
              style={{ paddingBottom: Math.max(insets.bottom, theme.spacing['spacing.general.x4']) }}
            >
              <Button
                disabled={!s.formState.isValid}
                loading={s.flowRest.isGeoCodeAddressFetching}
                variant="primary"
                onPress={s.flowRest.onPrimaryPress}
              >
                <Text color="surface.default" textAlign="center">
                  {current === max - 1 ? tOnboarding('confirm') : tOnboarding('nextStep')}
                </Text>
              </Button>

              {s.flowRest.showChangePostal ? (
                <Button variant="whiteWithBorder" onPress={s.flowRest.resetToPostalInput}>
                  <Text textAlign="center">{s.t('changePostalCode')}</Text>
                </Button>
              ) : null}
            </Column>
          </Column>
        </View>

        <SignUpSheet
          onLogin={() => navigation.reset({ routes: [{ name: 'main' }] })}
          state={s.signUpSheetState}
          source={SignUpSource.ContactInformation}
        />
      </Column>
    </KeyboardAvoidingView>
  );
};

export const ContactInformationScreen: NavigationScreen<'contact-information'> = withErrorBoundary(
  ContactInformationScreenComponent,
);
